import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, MapPin, Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { fireConfetti } from "@/lib/confetti";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

const CONTACT_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Spin Labs",
    url: "https://spinlabs.in/contact",
    description: "Tell Spin Labs about your project. We reply within 24 hours.",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Spin Labs",
    url: "https://spinlabs.in",
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "info@spinlabs.in",
        availableLanguage: ["English", "Hindi"],
        areaServed: "Worldwide",
      },
    ],
  },
  breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ]),
];

// Same fallback rule as lib/api.js — empty/missing env → same-origin "/api".
const RAW_BACKEND = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
const API = RAW_BACKEND ? `${RAW_BACKEND}/api` : "/api";

const SERVICE_OPTIONS = [
  "Mobile App Development",
  "AI-Powered Website",
  "Custom AI System",
  "Software Development",
  "SEO Engineering",
  "Marketing",
  "SaaS Platform",
  "Not Sure Yet",
];

const BUDGET_OPTIONS = ["< $10k", "$10k – $25k", "$25k – $75k", "$75k – $200k", "$200k+"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", budget: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.message.length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        service: form.service,
        budget: form.budget || null,
        message: form.message,
      });
      setSuccess(true);
      // Delight moment — neon confetti burst from the form button location
      const btn = document.querySelector('[data-testid="contact-submit"]');
      const rect = btn ? btn.getBoundingClientRect() : null;
      fireConfetti({
        originX: rect ? rect.left + rect.width / 2 : undefined,
        originY: rect ? rect.top + rect.height / 2 : undefined,
        count: 160,
      });
      toast.success("Brief received. Check your inbox for confirmation.");
      setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Start a Project"
        path="/contact"
        description="Tell Spin Labs about your project. We reply to every brief within 24 hours — usually faster. Mobile, AI, SaaS, software."
        keywords="contact Spin Labs, hire software studio, start a project, AI agency contact"
        jsonLd={CONTACT_JSONLD}
      />
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="spotlight w-[600px] h-[600px] bg-[#FF00FF]/15 top-0 left-10" />
        <div className="spotlight w-[500px] h-[500px] bg-[#00F0FF]/15 bottom-0 right-10" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6">Contact</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.9] text-balance max-w-5xl">
              Let's build something <span className="gradient-text">electric.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-white/60 leading-relaxed">
              Tell us about your project. We reply to every message within 24 hours — usually faster.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <div className="relative rounded-3xl border border-white/10 bg-[#0A0A0C] p-8 lg:p-10 overflow-hidden">
              <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#00F0FF]/5 blur-3xl" />
              <div className="relative">
                {success ? (
                  <div data-testid="contact-success" className="py-10 text-center">
                    <div className="mx-auto w-16 h-16 rounded-full border border-[#39FF14]/50 bg-[#39FF14]/10 flex items-center justify-center mb-5">
                      <CheckCircle2 className="w-8 h-8 text-[#39FF14]" />
                    </div>
                    <h3 className="font-display text-2xl lg:text-3xl font-bold">Transmission received</h3>
                    <p className="mt-3 text-white/60 max-w-md mx-auto">
                      We've sent a confirmation email to your inbox and a human at Spin Labs is already reading your brief. Expect a reply within 24 hours with next steps.
                    </p>
                    <button
                      data-testid="contact-reset"
                      onClick={() => setSuccess(false)}
                      className="mt-8 btn-secondary"
                    >
                      Send another <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Name *</Label>
                        <Input
                          id="name"
                          data-testid="contact-name"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          placeholder="Ada Lovelace"
                          className="mt-2 bg-black/40 border-white/10 h-12 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          placeholder="ada@company.com"
                          className="mt-2 bg-black/40 border-white/10 h-12 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="company" className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Company</Label>
                      <Input
                        id="company"
                        data-testid="contact-company"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder="Acme Corp"
                        className="mt-2 bg-black/40 border-white/10 h-12 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Service *</Label>
                        <Select value={form.service} onValueChange={(v) => update("service", v)}>
                          <SelectTrigger data-testid="contact-service" className="mt-2 h-12 bg-black/40 border-white/10 text-white">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0C] border-white/10 text-white">
                            {SERVICE_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s} className="focus:bg-[#00F0FF]/10 focus:text-[#00F0FF]">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Budget</Label>
                        <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                          <SelectTrigger data-testid="contact-budget" className="mt-2 h-12 bg-black/40 border-white/10 text-white">
                            <SelectValue placeholder="Rough range" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0C] border-white/10 text-white">
                            {BUDGET_OPTIONS.map((b) => (
                              <SelectItem key={b} value={b} className="focus:bg-[#00F0FF]/10 focus:text-[#00F0FF]">{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Project Brief *</Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        placeholder="Tell us what you're building, who it's for, and when you need it live."
                        className="mt-2 bg-black/40 border-white/10 text-white min-h-[160px] focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="btn-primary !w-full !py-4 !text-base disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          Send Transmission <ArrowUpRight className="w-5 h-5" />
                        </>
                      )}
                    </button>

                    <p className="text-xs font-mono-neo text-white/40 text-center">
                      * Required. We reply within 24 hours. No spam, ever.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2 space-y-5"
          >
            {[
              { i: Mail, t: "Email", v: "info@spinlabs.in", color: "#00F0FF" },
              { i: MapPin, t: "HQ", v: "Delhi, IN · Remote", color: "#39FF14" },
            ].map((c, i) => (
              <div key={i} className="p-6 rounded-2xl border border-white/10 hover:border-[#00F0FF]/40 transition-colors">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center border mb-4"
                     style={{ borderColor: `${c.color}40`, background: `${c.color}10` }}>
                  <c.i className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-white/40">{c.t}</div>
                <div className="font-display text-lg font-bold mt-1">{c.v}</div>
              </div>
            ))}

            <div className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#00F0FF]/5 to-[#FF00FF]/5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
                <span className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#39FF14]">Accepting Projects</span>
              </div>
              <div className="font-display font-bold text-lg">Next availability: Q3 2026</div>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                Two engagement slots open for Q2 2026. Reserve yours with a project brief above.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
